import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import type { Identifier, RaRecord } from "react-admin";
import {
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  Pagination,
  ResourceContextProvider,
  TextField,
  useNotify,
} from "react-admin";
import type { ListControllerResult } from "ra-core";
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { getApiUrl } from "@/constants";
import httpClient from "@/dataprovider/http-client";

import {
  addSelectableRowsToPending,
  mergePageSelectionIntoPending,
  searchPageIndices,
  SELECT_ALL_MATCHING_MAX_PAGES,
  SELECT_ALL_MATCHING_PAGE_SIZE,
  shouldShowSelectAllMatching,
  thalisSearchParams,
} from "./add-thalis-selection";

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_SEARCH_ROWS_PER_PAGE = 25;
const SEARCH_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/** Same outlined height for search TextField and tag Autocomplete (MUI small). */
function filterControlSx(theme: Theme, { allowGrow }: { allowGrow: boolean }) {
  const padY = allowGrow ? theme.spacing(0.5) : 0;
  const padX = theme.spacing(1.75);
  return {
    "& .MuiOutlinedInput-root": {
      boxSizing: "border-box" as const,
      minHeight: theme.spacing(5),
      height: allowGrow ? "auto" : theme.spacing(5),
      padding: `${padY} ${padX} !important`,
      alignItems: "center",
    },
    "& .MuiInputBase-input": {
      py: 0,
    },
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
    },
  };
}

/** Thali row as a react-admin record (Datagrid / fields). */
const THALI_SEARCH_RESOURCE = "fmbData";

export interface FmbThaliRow {
  id?: string;
  thaliNo?: string;
  deliveryAddress?: string | null;
  deliveryMohallah?: string | null;
  thaliType?: { id?: string; name?: string | null; code?: string | null } | null;
  fmb?: { itsNo?: string; name?: string };
  /** Serialized from API (`fmbThaliTag` rows). */
  tags?: string[];
}

type ThaliSearchRecord = FmbThaliRow & RaRecord;

export function tagsCell(tags: string[] | undefined) {
  if (!tags?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.25 }}>
      {tags.map((tag) => (
        <Chip key={tag} label={tag} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

function rowsFromResponse(json: unknown): unknown[] {
  if (
    json &&
    typeof json === "object" &&
    "rows" in json &&
    Array.isArray((json as { rows: unknown }).rows)
  ) {
    return (json as { rows: unknown[] }).rows;
  }
  return [];
}

function parseThaliSearchResponse(json: unknown): {
  rows: FmbThaliRow[];
  totalCount: number;
  page: number;
  pageSize: number;
} {
  const rows = rowsFromResponse(json) as FmbThaliRow[];
  if (json && typeof json === "object") {
    const o = json as {
      totalCount?: unknown;
      page?: unknown;
      pageSize?: unknown;
    };
    const totalCount = typeof o.totalCount === "number" ? o.totalCount : rows.length;
    const page = typeof o.page === "number" ? o.page : 0;
    const pageSize = typeof o.pageSize === "number" ? o.pageSize : DEFAULT_SEARCH_ROWS_PER_PAGE;
    return { rows, totalCount, page, pageSize };
  }
  return {
    rows,
    totalCount: rows.length,
    page: 0,
    pageSize: DEFAULT_SEARCH_ROWS_PER_PAGE,
  };
}

function emptyListStubs(): Pick<
  ListControllerResult,
  | "defaultTitle"
  | "displayedFilters"
  | "exporter"
  | "filter"
  | "filterValues"
  | "hideFilter"
  | "showFilter"
  | "setFilters"
  | "sort"
  | "resource"
> {
  return {
    sort: { field: "thaliNo", order: "ASC" },
    defaultTitle: "",
    displayedFilters: {},
    exporter: false,
    filter: {},
    filterValues: {},
    hideFilter: () => {},
    showFilter: () => {},
    setFilters: () => {},
    resource: "",
  };
}

export type AddThalisDialogProps = {
  open: boolean;
  onClose: () => void;
  distributorId: string | number | undefined;
  /** Thalis already linked to this distributor — not selectable. */
  assignedThaliIdSet: Set<string>;
  /** Parent loading / mutating assignments (disables dialog actions). */
  busy: boolean;
  onAssignSuccess: () => Promise<void>;
};

export default function AddThalisDialog({
  open,
  onClose,
  distributorId,
  assignedThaliIdSet,
  busy,
  onAssignSuccess,
}: AddThalisDialogProps) {
  const notify = useNotify();

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tagFilterInput, setTagFilterInput] = useState<string[]>([]);
  const [debouncedTagFilter, setDebouncedTagFilter] = useState<string[]>([]);
  const [tagSuggestOptions, setTagSuggestOptions] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const [thaliSearchResults, setThaliSearchResults] = useState<FmbThaliRow[]>([]);
  const [thaliSearchTotal, setThaliSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(0);
  const [searchRowsPerPage, setSearchRowsPerPage] = useState(DEFAULT_SEARCH_ROWS_PER_PAGE);
  const [pendingAssign, setPendingAssign] = useState<Record<string, FmbThaliRow>>({});
  const dialogOpenRef = useRef(open);
  dialogOpenRef.current = open;

  const disabled = busy || assignSubmitting || selectingAll;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedTagFilter([...tagFilterInput]),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(t);
  }, [tagFilterInput]);

  const debouncedTagsSignature = useMemo(() => debouncedTagFilter.join("\0"), [debouncedTagFilter]);

  useEffect(() => {
    setSearchPage(0);
  }, [debouncedSearch, debouncedTagsSignature]);

  const loadTagSuggestions = useCallback(async (q: string) => {
    try {
      const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      const { json } = await httpClient(
        `${getApiUrl()}/fmbThaliDistribution/thali-tag-suggestions${qs}`
      );
      const rows = (json as { rows?: string[] }).rows ?? [];
      setTagSuggestOptions(rows);
    } catch {
      setTagSuggestOptions([]);
    }
  }, []);

  const debouncedTagSuggest = useMemo(
    () => debounce((q: string) => void loadTagSuggestions(q), 250),
    [loadTagSuggestions]
  );

  useEffect(() => () => debouncedTagSuggest.cancel(), [debouncedTagSuggest]);

  const fetchThalis = useCallback(
    async (opts: { search: string; tags: string[]; page: number; pageSize: number }) => {
      if (!distributorId) return;
      setSearchLoading(true);
      try {
        const params = thalisSearchParams(opts);
        const res = await httpClient(
          `${getApiUrl()}/fmbThaliDistribution/thalis?${params.toString()}`,
          {
            method: "GET",
          }
        );
        const parsed = parseThaliSearchResponse(res.json);
        setThaliSearchResults(parsed.rows);
        setThaliSearchTotal(parsed.totalCount);
      } catch (e: unknown) {
        notify(e instanceof Error ? e.message : "Failed to search thalis", { type: "warning" });
      } finally {
        setSearchLoading(false);
      }
    },
    [distributorId, notify]
  );

  useEffect(() => {
    if (!open) return;
    void fetchThalis({
      search: debouncedSearch,
      tags: debouncedTagFilter,
      page: searchPage,
      pageSize: searchRowsPerPage,
    });
  }, [
    open,
    debouncedSearch,
    debouncedTagsSignature,
    searchPage,
    searchRowsPerPage,
    fetchThalis,
    debouncedTagFilter,
  ]);

  const searchRecords = useMemo(
    (): ThaliSearchRecord[] =>
      thaliSearchResults
        .filter((t): t is FmbThaliRow & { id: string } => Boolean(t.id))
        .map((t) => ({ ...t, id: t.id })),
    [thaliSearchResults]
  );

  const selectedIds = useMemo(() => Object.keys(pendingAssign) as Identifier[], [pendingAssign]);

  const onSelect = useCallback(
    (ids: Identifier[]) => {
      setPendingAssign((prev) =>
        mergePageSelectionIntoPending(prev, thaliSearchResults, ids, assignedThaliIdSet)
      );
    },
    [thaliSearchResults, assignedThaliIdSet]
  );

  const onToggleItem = useCallback(
    (id: Identifier) => {
      const sid = String(id);
      setPendingAssign((prev) => {
        const next = { ...prev };
        if (next[sid]) {
          delete next[sid];
          return next;
        }
        const row = thaliSearchResults.find((t) => String(t.id) === sid);
        if (row && !assignedThaliIdSet.has(sid)) next[sid] = row;
        return next;
      });
    },
    [thaliSearchResults, assignedThaliIdSet]
  );

  const clearPendingAssign = useCallback(() => setPendingAssign({}), []);

  const setListPage = useCallback((pageOneBased: number) => {
    setSearchPage(Math.max(0, pageOneBased - 1));
  }, []);

  const setListPerPage = useCallback((n: number | string) => {
    const v = typeof n === "string" ? Number.parseInt(n, 10) : n;
    if (!Number.isFinite(v)) return;
    setSearchRowsPerPage(v);
    setSearchPage(0);
  }, []);

  const thaliSearchListContext = useMemo((): ListControllerResult<ThaliSearchRecord> => {
    const stubs = emptyListStubs();
    const page = searchPage + 1;
    const perPage = searchRowsPerPage;
    const total = thaliSearchTotal;
    const initialLoad = searchLoading && thaliSearchResults.length === 0;

    const base = {
      ...stubs,
      getData: async () => searchRecords,
      onSelect,
      onSelectAll: () => {},
      onToggleItem,
      onUnselectItems: clearPendingAssign,
      page,
      perPage,
      refetch: () => {
        void fetchThalis({
          search: debouncedSearch,
          tags: debouncedTagFilter,
          page: searchPage,
          pageSize: searchRowsPerPage,
        });
      },
      selectedIds,
      setPage: setListPage,
      setPerPage: setListPerPage,
      setSort: () => {},
      hasNextPage: total > 0 && page * perPage < total,
      hasPreviousPage: page > 1,
      isFetching: searchLoading,
      isLoading: false,
      isPaused: false,
      isPlaceholderData: false,
    };

    if (initialLoad) {
      return {
        ...base,
        isPending: true,
        data: undefined,
        total: undefined,
        meta: undefined,
        error: null,
      } as ListControllerResult<ThaliSearchRecord>;
    }

    return {
      ...base,
      isPending: false,
      data: searchRecords,
      total,
      meta: undefined,
      error: null,
    } as ListControllerResult<ThaliSearchRecord>;
  }, [
    searchPage,
    searchRowsPerPage,
    thaliSearchTotal,
    searchRecords,
    searchLoading,
    thaliSearchResults.length,
    selectedIds,
    onSelect,
    onToggleItem,
    clearPendingAssign,
    fetchThalis,
    debouncedSearch,
    debouncedTagFilter,
    setListPage,
    setListPerPage,
  ]);

  useEffect(() => {
    setPendingAssign((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of assignedThaliIdSet) {
        if (next[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [assignedThaliIdSet]);

  const pendingAssignList = useMemo(
    () => Object.entries(pendingAssign).map(([id, row]) => ({ id, row })),
    [pendingAssign]
  );

  useEffect(() => {
    if (open) return;
    setSearchInput("");
    setDebouncedSearch("");
    setTagFilterInput([]);
    setDebouncedTagFilter([]);
    setSearchPage(0);
    setPendingAssign({});
    setSelectingAll(false);
  }, [open]);

  const selectAllMatching = async () => {
    if (!distributorId || thaliSearchTotal === 0) return;
    setSelectingAll(true);
    try {
      const pageSize = SELECT_ALL_MATCHING_PAGE_SIZE;
      const pages = searchPageIndices(thaliSearchTotal, pageSize, SELECT_ALL_MATCHING_MAX_PAGES);
      const allRows: FmbThaliRow[] = [];
      let reportedTotal = thaliSearchTotal;
      for (const page of pages) {
        if (!dialogOpenRef.current) return;
        const params = thalisSearchParams({
          search: debouncedSearch,
          tags: debouncedTagFilter,
          page,
          pageSize,
        });
        const res = await httpClient(
          `${getApiUrl()}/fmbThaliDistribution/thalis?${params.toString()}`,
          { method: "GET" }
        );
        const parsed = parseThaliSearchResponse(res.json);
        reportedTotal = parsed.totalCount;
        allRows.push(...parsed.rows);
        if (allRows.length >= parsed.totalCount) break;
      }
      if (!dialogOpenRef.current) return;
      setPendingAssign((prev) => addSelectableRowsToPending(prev, allRows, assignedThaliIdSet));
      const selectable = allRows.filter((r) => {
        const id = r.id != null && r.id !== "" ? String(r.id) : "";
        return id && !assignedThaliIdSet.has(id);
      }).length;
      const capped = reportedTotal > SELECT_ALL_MATCHING_PAGE_SIZE * SELECT_ALL_MATCHING_MAX_PAGES;
      notify(
        capped
          ? `Selected ${selectable.toLocaleString()} thalis (first ${SELECT_ALL_MATCHING_MAX_PAGES} pages)`
          : `Selected ${selectable.toLocaleString()} matching thali${selectable === 1 ? "" : "s"}`,
        { type: "info" }
      );
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : "Failed to select matching thalis", {
        type: "error",
      });
    } finally {
      setSelectingAll(false);
    }
  };

  const doAssign = async () => {
    const ids = Object.keys(pendingAssign);
    if (!ids.length) {
      notify("Select at least one thali", { type: "warning" });
      return;
    }
    if (!distributorId) return;
    setAssignSubmitting(true);
    try {
      await httpClient(`${getApiUrl()}/fmbThaliDistribution/assign`, {
        method: "POST",
        body: JSON.stringify({ distributorId, fmbThaliIds: ids }),
      });
      notify(`Assigned ${ids.length} thali(s)`, { type: "info" });
      setPendingAssign({});
      onClose();
      await onAssignSuccess();
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : "Assign failed", { type: "error" });
    } finally {
      setAssignSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle>Add thalis</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
          >
            <Box sx={{ flex: { sm: "1 1 0" }, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
              <MuiTextField
                size="small"
                margin="none"
                hiddenLabel
                fullWidth
                placeholder="Search thali no, ITS, name, or address…"
                aria-label="Search thalis"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                disabled={disabled}
                sx={(theme) => filterControlSx(theme, { allowGrow: false })}
              />
            </Box>
            <Box
              sx={(theme) => ({
                flex: { sm: "0 0 auto" },
                width: { xs: "100%", sm: theme.spacing(35) },
                minWidth: 0,
              })}
            >
              <Autocomplete<string, true, false, true>
                multiple
                freeSolo
                fullWidth
                size="small"
                forcePopupIcon={false}
                options={tagSuggestOptions}
                value={tagFilterInput}
                onChange={(_, raw) => {
                  const next = (Array.isArray(raw) ? raw : [])
                    .map((x) => String(x).trim())
                    .filter(Boolean);
                  const seen = new Set<string>();
                  const deduped: string[] = [];
                  for (const t of next) {
                    const k = t.toLowerCase();
                    if (seen.has(k)) continue;
                    seen.add(k);
                    deduped.push(t);
                    if (deduped.length >= 20) break;
                  }
                  setTagFilterInput(deduped);
                }}
                onInputChange={(_, input, reason) => {
                  if (reason === "reset") return;
                  if (reason === "clear") {
                    setTagFilterInput([]);
                    debouncedTagSuggest.cancel();
                    return;
                  }
                  if (reason === "input") debouncedTagSuggest(input);
                }}
                onOpen={() =>
                  void loadTagSuggestions(tagFilterInput[tagFilterInput.length - 1] ?? "")
                }
                filterSelectedOptions
                disabled={disabled}
                sx={(theme) => filterControlSx(theme, { allowGrow: tagFilterInput.length > 0 })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                      size="small"
                      label={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    size="small"
                    margin="none"
                    hiddenLabel
                    placeholder="Filter by tag"
                    aria-label="Filter by tag"
                  />
                )}
              />
            </Box>
          </Stack>
          {searchLoading ||
          selectingAll ||
          shouldShowSelectAllMatching(thaliSearchTotal, searchRecords.length) ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={searchLoading || selectingAll ? "space-between" : "flex-end"}
              flexWrap="wrap"
              useFlexGap
            >
              {searchLoading || selectingAll ? (
                <Typography variant="caption" color="text.secondary">
                  {selectingAll ? "Selecting all matching…" : "Searching…"}
                </Typography>
              ) : null}
              {shouldShowSelectAllMatching(thaliSearchTotal, searchRecords.length) ? (
                <MuiButton
                  size="small"
                  variant="text"
                  disabled={disabled || searchLoading}
                  onClick={() => void selectAllMatching()}
                >
                  Select all {thaliSearchTotal.toLocaleString()} matching
                </MuiButton>
              ) : null}
            </Stack>
          ) : null}

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              maxWidth: "100%",
            }}
          >
            <ResourceContextProvider value={THALI_SEARCH_RESOURCE}>
              <ListContextProvider value={thaliSearchListContext}>
                <Box sx={{ overflowX: "auto", width: "100%" }}>
                  <Datagrid
                    bulkActionsToolbar={<Fragment />}
                    bulkActionButtons={<Fragment />}
                    rowClick={false}
                    resource={THALI_SEARCH_RESOURCE}
                    isRowSelectable={(r) => !assignedThaliIdSet.has(String(r.id))}
                    rowSx={(r) => ({
                      opacity: assignedThaliIdSet.has(String(r.id)) ? 0.5 : 1,
                    })}
                    empty={
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No thalis match this search.
                        </Typography>
                      </Box>
                    }
                    sx={{ minWidth: 520 }}
                  >
                    <TextField source="thaliNo" label="Thali no." sortable={false} />
                    <TextField source="fmb.itsNo" label="ITS" sortable={false} />
                    <TextField source="fmb.name" label="Name" sortable={false} />
                    <FunctionField
                      label="Address"
                      sortable={false}
                      render={(r: ThaliSearchRecord) => {
                        const parts = [r.deliveryAddress, r.deliveryMohallah]
                          .map((x) => (typeof x === "string" ? x.trim() : ""))
                          .filter(Boolean);
                        return parts.join(" — ") || "—";
                      }}
                    />
                    <FunctionField
                      label="Tags"
                      sortable={false}
                      render={(r: ThaliSearchRecord) => tagsCell(r.tags)}
                    />
                  </Datagrid>
                </Box>
                <Pagination rowsPerPageOptions={[...SEARCH_ROWS_PER_PAGE_OPTIONS]} />
              </ListContextProvider>
            </ResourceContextProvider>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" onClick={onClose} disabled={disabled} />
        <Button
          label={
            pendingAssignList.length > 0
              ? `Assign ${pendingAssignList.length} thali${pendingAssignList.length === 1 ? "" : "s"}`
              : "Assign"
          }
          variant="contained"
          onClick={() => void doAssign()}
          disabled={disabled || pendingAssignList.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
}
